import type { PropifyEntity } from '@/domain/base';
import type { EmailAddress } from '@/domain/emails';
import type { Lease } from '@/domain/lease';
import type { PartyRole, Person } from '@/domain/person';
import type { Phone } from '@/domain/phone';
import type { Property } from '@/domain/property';
import type { Unit } from '@/domain/unit';
import type { MonetaryAmount } from '@propify/components';

export enum ResidentStatus {
  CURRENT = 'CURRENT',
  FUTURE = 'FUTURE',
  NOTICE = 'NOTICE',
  EVICTION = 'EVICTION',
  PAST = 'PAST',
  INACTIVE = 'INACTIVE',
  NON_RENEWAL = 'NON_RENEWAL',
  HOLDOVER = 'HOLDOVER',
  CANCELLED = 'CANCELLED',
}

export enum ResidentType {
  PRIMARY = 'PRIMARY',
  COSIGNER = 'COSIGNER',
}

export interface Resident extends PartyRole {
  status: ResidentStatus;
  type: ResidentType;
  firstName: string;
  lastName: string;
  salutation?: string;
  phones?: Phone[];
  tenantType?: string;
  balance?: MonetaryAmount;
  writeOffBalance?: MonetaryAmount;
  securityDepositCollected?: MonetaryAmount;
  moveInDate?: string;
  moveOutDate?: string;
  moveOutReason?: string;
  smartHomeEnabledDate?: string;
  deposits?: Deposit[];
  emailAddresses?: EmailAddress[];
  unitId: number;
  unit?: Unit;
  renewable?: boolean;
  rentalPrice?: MonetaryAmount;
  subsidy?: boolean;
  collectionExternalLink?: string;
  foreignDbCode?: string;
  foreignDbId?: number;
  foreignDbResidentUrl?: string;
  foreignDbLedgerUrl?: string;
}

export interface ResidentSearchResult extends Resident {
  person?: Person;
  lease?: Lease;
  property?: Property;
}

export interface ResidentSearchParams {
  id?: number | number[];
  personId?: number;
  propertyId?: number;
  unitId?: number;
  keywords?: string;
  status?: ResidentStatus;
  type?: ResidentType[];
}

export interface ScheduledCharge extends PropifyEntity {
  chargeType?: ChargeType;
  startDate?: string;
  endDate?: string;
  amount?: MonetaryAmount;
  residentId?: number;
  leaseId?: number;
  foreignDbCode?: string;
  foreignDbType?: string;
}

export interface ScheduledChargesSearchResult extends ScheduledCharge {
  resident: Resident;
}

export interface ScheduledChargesSearchParams {
  scheduledChargeId?: number;
  propertyId?: number;
  residentId?: number | number[];
  unitId?: number;
}

interface ChargeType extends PropifyEntity {
  name?: string;
}

interface Deposit extends PropifyEntity {
  depositType?: DepositType;
  amount?: MonetaryAmount;
  residentId?: number;
}

interface DepositType extends PropifyEntity {
  name?: string;
}

export interface Balance {
  outstandingBalance: MonetaryAmount;
}

export interface LedgerTransaction {
  id: string;
  date: string;
  code: string;
  description: string;
  notes: string;
  isCharge: boolean;
  chargeAmount: MonetaryAmount;
  paymentAmount: MonetaryAmount;
  balanceAmount: MonetaryAmount;
}

export interface MinNotifyBalance {
  minNotifyBalance: MonetaryAmount;
}

export interface MinBalanceThreshold {
  minBalanceThreshold: MonetaryAmount;
}
